import { useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring } from 'framer-motion';

/**
 * 숫자가 0 에서 올라가며 자리 잡는 카운터 (Magic UI NumberTicker 계열).
 *
 * 화면에 들어올 때 한 번만 돈다. 스프링이라 마지막 자리에서 부드럽게 멈춘다.
 * 자리수가 흔들리지 않게 쓰는 쪽이 `tabular-nums` 를 준다.
 *
 * **마크업은 완성된 숫자다.** 서버 HTML(크롤러 · 첫 페인트)이 0 을 내면 안 된다. 세어 올리는 건
 * hydration 뒤 스프링이 textContent 를 바꿔서 한다.
 */
export function NumberTicker({
  value,
  className,
  delay = 0,
}: {
  value: number;
  className?: string;
  /** 시작을 늦추는 초. 세 칸을 살짝 엇갈리게 띄울 때 쓴다. */
  delay?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { damping: 60, stiffness: 100 });
  const inView = useInView(ref, { once: true, margin: '0px' });

  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => motionValue.set(value), delay * 1000);
    return () => clearTimeout(t);
  }, [inView, value, delay, motionValue]);

  useEffect(
    () =>
      spring.on('change', (latest) => {
        if (ref.current) ref.current.textContent = Math.round(latest).toLocaleString('ko-KR');
      }),
    [spring],
  );

  return (
    <span ref={ref} className={className}>
      {value.toLocaleString('ko-KR')}
    </span>
  );
}
